// Hand-crafted BattleResult that exercises every event type in the
// discriminated union. The shape is type-checked against the shared
// schema and validated at module load via Zod, so any drift in either
// the schema or this fixture surfaces immediately.
//
// TODO: regenerate from a real simulator run once the simulator emits
// synergies / triggers / heals / summons / status effects (currently
// it only emits battle_start / turn_start / strike / damage / crumple
// / slide_forward / turn_end / battle_end).

import { BattleResultSchema, type BattleResult } from '@origami/shared'

const raw: BattleResult = {
  winner: 'A',
  surviving_count: 2,
  event_log: [
    // ---- turn 0: setup ----
    {
      turn: 0,
      type: 'battle_start',
      teams: {
        A: [
          { ref: 'A:1', unit_id: 'crane_flock',  star: 2, hp: 7, atk: 6, position: 1 },
          { ref: 'A:2', unit_id: 'spring_frog',  star: 2, hp: 4, atk: 3, position: 2 },
          { ref: 'A:3', unit_id: 'kabuto_buddy', star: 1, hp: 4, atk: 2, position: 3 },
          { ref: 'A:4', unit_id: 'koi_chan',     star: 1, hp: 3, atk: 4, position: 4 },
        ],
        B: [
          { ref: 'B:1', unit_id: 'tora',         star: 2, hp: 6, atk: 5, position: 1 },
          { ref: 'B:2', unit_id: 'adder',        star: 1, hp: 3, atk: 2, position: 2 },
          { ref: 'B:3', unit_id: 'tora',         star: 1, hp: 3, atk: 3, position: 3 },
          { ref: 'B:4', unit_id: 'paper_mite',   star: 1, hp: 2, atk: 2, position: 4 },
        ],
      },
    },
    {
      turn: 0, type: 'synergy_applied',
      team: 'A', axis: 'element', key: 'wind', threshold: 2,
      description: 'Wind ×2 — +1 ATK to wind allies',
    },
    {
      turn: 0, type: 'synergy_applied',
      team: 'A', axis: 'animal', key: 'reptile', threshold: 2,
      description: 'Reptile ×2 — +1 HP to reptile allies',
    },
    {
      turn: 0, type: 'synergy_applied',
      team: 'B', axis: 'element', key: 'fire', threshold: 3,
      description: 'Fire ×3 — +2 ATK to fire allies',
    },

    // ---- turn 1: opening triggers + first strike ----
    { turn: 1, type: 'turn_start' },
    {
      turn: 1, type: 'trigger_fired',
      source: 'A:1', trigger: 'on_unfold',
      effect: 'Crane V-formation — A:2 +1 ATK',
    },
    {
      turn: 1, type: 'stat_changed',
      target: 'A:2', stat: 'atk', delta: 1, new_value: 4, permanent: true,
    },
    // wind (A:1 crane) vs fire (B:1 tora) → 1.25× the wind way
    { turn: 1, type: 'strike', source: 'A:1', target: 'B:1', damage: 8, multiplier: 1.25 },
    { turn: 1, type: 'strike', source: 'B:1', target: 'A:1', damage: 4, multiplier: 0.75 },
    { turn: 1, type: 'damage', source: 'A:1', target: 'B:1', amount: 8, new_hp: -2, multiplier: 1.25 },
    { turn: 1, type: 'damage', source: 'B:1', target: 'A:1', amount: 4, new_hp: 3, multiplier: 0.75 },
    { turn: 1, type: 'crumple', unit: 'B:1' },
    {
      turn: 1, type: 'trigger_fired',
      source: 'B:1', trigger: 'on_crumple',
      effect: 'Tora roar — B:2 enraged + A:3 −1 ATK',
    },
    {
      turn: 1, type: 'status_applied',
      target: 'B:2', status: 'enraged', stacks: 1,
    },
    // Debuff event — exercises the .buffed-down pulse alongside the
    // .buffed-up from earlier in this turn so the demo shows both
    // pulse variants. Permanent so the stat sticks.
    {
      turn: 1, type: 'stat_changed',
      target: 'A:3', stat: 'atk', delta: -1, new_value: 1, permanent: true,
    },
    {
      turn: 1, type: 'slide_forward', team: 'B',
      new_order: ['B:2', 'B:3', 'B:4'],
    },
    { turn: 1, type: 'turn_end' },

    // ---- turn 2: second exchange + heal ability ----
    { turn: 2, type: 'turn_start' },
    // adder (B:2, fire) vs crane (A:1, wind) — fire beats wind 1.25×
    { turn: 2, type: 'strike', source: 'A:1', target: 'B:2', damage: 5, multiplier: 0.75 },
    { turn: 2, type: 'strike', source: 'B:2', target: 'A:1', damage: 4, multiplier: 1.25 },
    { turn: 2, type: 'damage', source: 'A:1', target: 'B:2', amount: 5, new_hp: -2, multiplier: 0.75 },
    { turn: 2, type: 'damage', source: 'B:2', target: 'A:1', amount: 4, new_hp: -1, multiplier: 1.25 },
    { turn: 2, type: 'crumple', unit: 'B:2' },
    { turn: 2, type: 'crumple', unit: 'A:1' },
    {
      turn: 2, type: 'trigger_fired',
      source: 'A:4', trigger: 'on_ally_crumple',
      effect: 'Koi mercy heal — A:2 +3 HP',
    },
    { turn: 2, type: 'heal', source: 'A:4', target: 'A:2', amount: 3, new_hp: 7 },
    { turn: 2, type: 'slide_forward', team: 'A', new_order: ['A:2', 'A:3', 'A:4'] },
    { turn: 2, type: 'slide_forward', team: 'B', new_order: ['B:3', 'B:4'] },
    { turn: 2, type: 'turn_end' },

    // ---- turn 3: ability damage + summon ----
    { turn: 3, type: 'turn_start' },
    // wood (A:2 frog) vs fire (B:3 tora) — wood loses to fire 0.75×
    { turn: 3, type: 'strike', source: 'A:2', target: 'B:3', damage: 3, multiplier: 0.75 },
    { turn: 3, type: 'strike', source: 'B:3', target: 'A:2', damage: 4, multiplier: 1.25 },
    { turn: 3, type: 'damage', source: 'A:2', target: 'B:3', amount: 3, new_hp: 0, multiplier: 0.75 },
    { turn: 3, type: 'damage', source: 'B:3', target: 'A:2', amount: 4, new_hp: 3, multiplier: 1.25 },
    { turn: 3, type: 'crumple', unit: 'B:3' },
    {
      turn: 3, type: 'trigger_fired',
      source: 'A:3', trigger: 'on_ally_strike',
      effect: 'Beetle horn dust — B:4 takes 2',
    },
    // Ability damage: no multiplier per §7.7.
    { turn: 3, type: 'damage', source: 'A:3', target: 'B:4', amount: 2, new_hp: 0 },
    { turn: 3, type: 'crumple', unit: 'B:4' },
    {
      turn: 3, type: 'trigger_fired',
      source: 'A:3', trigger: 'on_unfold',
      effect: 'Beetle summons brood — paper_mite @5',
    },
    {
      turn: 3, type: 'summon',
      team: 'A', ref: 'A:5', unit_id: 'paper_mite', position: 5, stats: { hp: 2, atk: 2 },
    },
    { turn: 3, type: 'slide_forward', team: 'B', new_order: [] },
    { turn: 3, type: 'turn_end' },

    // ---- battle_end: A wins with two survivors ----
    {
      turn: 3, type: 'battle_end', winner: 'A', surviving_count: 3,
    },
  ],
}

// Validate now — fail loudly at boot if anything drifted.
export const demoBattle: BattleResult = BattleResultSchema.parse(raw)

/** Display metadata that doesn't fit in the BattleResult schema. */
export const demoBattleMeta = {
  round: 6,
  pairing: 3,
  seed: '0xA3F1',
  playerA: { name: 'Hokusai', tag: '@you',   maxHp: 30, hp: 22, record: '4-1', mascot: 'frog'  as const },
  playerB: { name: 'Sen no Ri', tag: '@ghost', maxHp: 30, hp: 18, record: '3-2', mascot: 'tiger' as const },
  /** Which side the current viewer is on, if any. 'A' / 'B' tags
   *  that side's label with "· you"; null in spectator mode (neither
   *  team belongs to the viewer). */
  viewerSide: 'A' as 'A' | 'B' | null,
  /** Epoch ms when the next round will start. Only set in live multi
   *  battles — the demo fixture stays null so the intermission pill
   *  doesn't render here. Typed nullable so BattleMeta consumers can
   *  pass `null` when there's no follow-up round. */
  nextRoundAtMs: null as number | null,
  /** Glyph key per unit_id for rendering — falls back to a heuristic
   *  if a unit_id isn't here. Mapped to glyph keys that have tiered
   *  bitmap art registered in TIERED_GLYPHS so the BattleScreen
   *  shows the photo-quality folded paper figures rather than SVG
   *  silhouettes. Thematic best-fits where there's no direct match:
   *    crane_flock  → flock      (crane flock = flock)
   *    spring_frog  → tadpole    (frog/tadpole)
   *    kabuto_buddy → mantis     (insect, no kabuto bitmap)
   *    koi_chan     → koi        (direct)
   *    paper_mite   → mite       (direct)
   *    tora         → fox        (predator mammal, no tiger bitmap)
   *    adder        → salamander (reptile/amphibian)
   */
  unitGlyphs: {
    crane_flock:  'flock',
    spring_frog:  'tadpole',
    kabuto_buddy: 'mantis',
    koi_chan:     'koi',
    paper_mite:   'mite',
    tora:         'fox',
    adder:        'salamander',
    // Token entries — production builds these from unitsById, but the
    // demo fixture has a hardcoded map so summoned tokens render
    // correctly in /?battle previews instead of falling back to the
    // 'crane' system silhouette.
    paper_mite_token:   'mite',
    pocket_mouse_token: 'mouse',
  } as Record<string, string>,
  /** Display names per unit_id. Used by the chronicle to render
   *  `[A:1]` / `[B:2]` tokens as team-tinted names instead of raw
   *  refs. Falls back to the ref when a unit_id isn't here. */
  unitNames: {
    crane_flock:  'Shadow Crane',
    spring_frog:  'Spring Frog',
    kabuto_buddy: 'Kabuto Buddy',
    koi_chan:     'Koi-chan',
    paper_mite:   'Paper Mite',
    tora:         'Tora',
    adder:        'Adder',
  } as Record<string, string>,
  /** Per-unit-id battle-render hints copied from each UnitDef.
   *  `unitFlying` true → BattleUnit lifts the figure ~28px higher
   *  off the ground and applies a +10° forward tilt at strike peak
   *  so the lunge reads as an airborne dive. `unitBodyOffsetY` is
   *  positive-up pixel offset for per-unit baseline correction
   *  (when a glyph's silhouette doesn't sit flush with the bottom
   *  of its TieredCritter bbox). Both fall back to safe defaults
   *  when missing. */
  unitFlying: {
    crane_flock: true,
  } as Record<string, boolean>,
  unitBodyOffsetY: {
    paper_mite: -15,
    paper_mite_token: -15,
  } as Record<string, number>,
  unitBodyScale: {
    paper_mite: 0.75,
    paper_mite_token: 0.6,
    pocket_mouse_token: 0.6,
  } as Record<string, number>,
  /** Per-unit-id element / animal lookups. Used by the impact-burst
   *  layer to element-tint ability projectiles and by the chronicle /
   *  synergy_tag inference to label synergy sources. Production builds
   *  these from the unit catalog; the demo fixture hardcodes them. */
  unitElements: {
    crane_flock:  'wind',
    spring_frog:  'water',
    kabuto_buddy: 'wood',
    koi_chan:     'water',
    paper_mite:   'earth',
    tora:         'fire',
    adder:        'water',
    paper_mite_token:   'earth',
    pocket_mouse_token: 'wind',
  } as Record<string, string>,
  unitAnimals: {
    crane_flock:  'bird',
    spring_frog:  'fish',
    kabuto_buddy: 'insect',
    koi_chan:     'fish',
    paper_mite:   'insect',
    tora:         'mammal',
    adder:        'reptile',
    paper_mite_token:   'insect',
    pocket_mouse_token: 'mammal',
  } as Record<string, string>,
}
